import React, { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import adminApi from '../api/adminApi';
import BilingualField from '../components/BilingualField';
import { MENU_ITEMS, getMenuConfig } from '../../lib/menuConfig';

const Card = ({ title, description, children }) => (
  <div className="bg-[#111111] border border-[rgba(212,175,55,0.15)] rounded-sm p-5 space-y-4">
    <div className="border-b border-[rgba(212,175,55,0.1)] pb-3">
      <h2 className="text-sm font-semibold text-white">{title}</h2>
      {description && <p className="text-white/40 text-xs mt-1">{description}</p>}
    </div>
    {children}
  </div>
);

const FooterEditorPage = () => {
  const [translations, setTranslations] = useState({ en: {}, ar: {} });
  const [globalSettings, setGlobalSettings] = useState({ menu: getMenuConfig() });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get('/admin/content').then(r => {
      setTranslations(r.data?.translations || { en: {}, ar: {} });
      const loadedGlobal = r.data?.global || {};
      setGlobalSettings({
        ...loadedGlobal,
        menu: getMenuConfig(loadedGlobal.menu),
      });
    }).catch(() => toast.error('Failed to load content'));
  }, []);

  const set = (lang, key, value) => {
    setTranslations(prev => ({
      ...prev,
      [lang]: { ...prev[lang], footer: { ...(prev[lang]?.footer || {}), [key]: value } }
    }));
  };

  const setMenuItemEnabled = (key, enabled) => {
    setGlobalSettings(prev => ({
      ...prev,
      menu: {
        ...getMenuConfig(prev.menu),
        [key]: {
          ...getMenuConfig(prev.menu)[key],
          enabled,
        },
      },
    }));
  };

  const bilingual = (label, key) => ({
    label,
    valueEn: translations.en?.footer?.[key] || '',
    valueAr: translations.ar?.footer?.[key] || '',
    onChangeEn: v => set('en', key, v),
    onChangeAr: v => set('ar', key, v),
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        adminApi.patch('/admin/content/translations', translations),
        adminApi.patch('/admin/content/global', {
          ...globalSettings,
          menu: getMenuConfig(globalSettings.menu),
        }),
      ]);
      toast.success('Footer and menu saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const menuConfig = getMenuConfig(globalSettings.menu);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Footer & Navigation</h1>
          <p className="text-white/40 text-sm mt-0.5">Footer text, menu labels, and section visibility</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-black font-semibold text-sm rounded-sm hover:bg-[#C5A028] transition-colors disabled:opacity-50">
          <Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <Card title="Footer Text" description="Brand tagline, copyright and group labels shown in the website footer.">
        <BilingualField {...bilingual('Brand Tagline', 'tagline')} multiline rows={3} />
        <BilingualField {...bilingual('"Part of" Label', 'partOf')} />
        <BilingualField {...bilingual('Group Name', 'group')} />
        <BilingualField {...bilingual('Rights Text', 'rights')} />
      </Card>

      <Card title="Navigation Labels" description="These labels are used in the header and footer menus.">
        <div className="space-y-4">
          {[
            { key: 'home', label: 'Home Link' },
            { key: 'about', label: 'About Link' },
            { key: 'products', label: 'Products Link' },
            { key: 'news', label: 'News Link' },
            { key: 'contact', label: 'Contact Link' },
          ].map(item => (
            <BilingualField
              key={item.key}
              label={item.label}
              valueEn={translations.en?.nav?.[item.key] || ''}
              valueAr={translations.ar?.nav?.[item.key] || ''}
              onChangeEn={v => setTranslations(prev => ({ ...prev, en: { ...prev.en, nav: { ...(prev.en?.nav || {}), [item.key]: v } } }))}
              onChangeAr={v => setTranslations(prev => ({ ...prev, ar: { ...prev.ar, nav: { ...(prev.ar?.nav || {}), [item.key]: v } } }))}
            />
          ))}
        </div>
      </Card>

      <Card title="Menu Visibility" description="Hide or show each section from the public website menu and homepage.">
        <div className="space-y-3">
          {MENU_ITEMS.map(item => {
            const enabled = menuConfig[item.key]?.enabled !== false;
            return (
              <div
                key={item.key}
                className="flex items-center justify-between gap-4 rounded-sm border border-[rgba(212,175,55,0.12)] bg-[#1A1A1A] px-4 py-3"
              >
                <div>
                  <p className="text-sm font-semibold text-white">{item.label}</p>
                  <p className="text-xs text-white/40 mt-1">{item.description}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setMenuItemEnabled(item.key, !enabled)}
                  className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
                    enabled ? 'bg-[#D4AF37]' : 'bg-white/10'
                  }`}
                  aria-pressed={enabled}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                      enabled ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                  <span className="sr-only">{enabled ? `Hide ${item.label}` : `Show ${item.label}`}</span>
                </button>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

export default FooterEditorPage;
